import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { NativeShareButton } from '@/components/common/native-share-button';
import QrCodeView from '@/components/common/qr-code-view';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useGroupQrCode from '@/hooks/groups/use-group-qrcode';
import { useBrand } from '@/hooks/pwa/use-brand';
import ROUTES from '@/routes';

export default function GroupQRCodeRoute() {
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.QR_CODE);
  const { data: group, isLoading: isGroupInfoLoading } = useGroupInfo(groupId);
  const { data: qrcode, isLoading: isGroupQrCodeLoading } = useGroupQrCode(groupId);
  const [qrcodeImage, setQrcodeImage] = useState<Blob | undefined>(undefined);

  const isLoading = isGroupInfoLoading || isGroupQrCodeLoading;

  return (
    <PageLayout
      title={t('conversation.setting.group.qr-code.title')}
      headerConfig={{
        navigateBack: true,
        actions: (
          <NativeShareButton
            isLoading={qrcodeImage === undefined}
            content={
              qrcodeImage && {
                type: 'qrcode',
                data: qrcodeImage,
              }
            }
          />
        ),
      }}>
      <LoadingLayout promise={isLoading}>
        {() =>
          qrcode ? (
            <QrCodeView
              data={qrcode}
              className='mb-safe-or-16'
              label={group?.GroupData?.GroupName}
              onCaptured={(blob) => {
                setQrcodeImage(blob);
              }}
              avatarUrl={group?.GroupData?.GroupImg}
              onForward={(url) => {
                navigate(
                  ROUTES.MESSAGES.GROUPS.SETTINGS.QR_CODE.$.SHARE.$buildPath({
                    relative: true,
                    searchParams: { code: url },
                  }),
                );
              }}>
              <p className='text-center mt-8 text-sm text-secondary desktop:mt-0'>
                {t('conversation.setting.group.qr-code.content', { brandName: brand.name })}
              </p>
            </QrCodeView>
          ) : null
        }
      </LoadingLayout>
    </PageLayout>
  );
}
